import React, { useCallback } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { type AttachmentSectionListProps } from '../../types/attachmentInterfaces';
import { AttachmentListItem } from './AttachmentListItem';
import { ATTACHMENT_COLORS } from '../../constants/attachmentColors';
import { AttachmentCarouselModal } from '../modals/AttachmentCarouselModal';
import { SavedAttachment } from '../../services/attachmentService';
import { useAttachmentCarousel } from '../../hooks/useAttachmentCarousel';
import { getSectionLabel } from '../../utils/sectionLabel';

/**
 * AttachmentSectionList component
 * Compact section-level attachment list for use in SubsectionForm.
 * Optionally shows table headers (controlled by showHeaders prop).
 */
export const AttachmentSectionList: React.FC<AttachmentSectionListProps> = ({
  attachments,
  isEditing,
  isMobile,
  onCaptionChange,
  onDelete,
  pendingCaptionEdits,
  showHeaders = true,
  offlineMode = false,
}) => {
  const { carouselProps, openCarousel } = useAttachmentCarousel();

  const handleOpenCarousel = useCallback((tapped: SavedAttachment) => {
    openCarousel(tapped, attachments, getSectionLabel(tapped.requestSection));
  }, [attachments, openCarousel]);
  if (attachments.length === 0) {
    return (
      <View style={styles.emptyContainer}>
        <Text style={styles.emptyText} testID="attachment-empty-message">No attachments</Text>
      </View>
    );
  }

  const layout = isMobile ? 'card' : 'table';

  return (
    <View style={styles.container} testID="attachment-list">
      {/* Desktop table headers (only if showHeaders is true and not mobile) */}
      {!isMobile && showHeaders && (
        <View style={styles.tableHeader}>
          <View style={styles.nameHeaderColumn}>
            <Text style={styles.headerText}>Name</Text>
          </View>
          <View style={styles.dateHeaderColumn}>
            <Text style={styles.headerText}>Date</Text>
          </View>
          <View style={styles.sizeHeaderColumn}>
            <Text style={styles.headerText}>Size</Text>
          </View>
          <View style={styles.actionsHeaderColumn}>
            <Text style={styles.headerText}>Actions</Text>
          </View>
        </View>
      )}

      {/* Attachment list */}
      <View style={styles.listContainer}>
        {attachments.map(attachment => (
          <AttachmentListItem
            key={attachment.id}
            attachment={attachment}
            layout={layout}
            isEditing={isEditing}
            isMobile={isMobile}
            onCaptionChange={onCaptionChange}
            onDelete={onDelete}
            pendingCaptionEdits={pendingCaptionEdits}
            showSectionBadge={false} // Never show badges in section view
            offlineMode={offlineMode}
            onOpenCarousel={handleOpenCarousel}
          />
        ))}
      </View>

      {/* Offline indicator */}
      {offlineMode && (
        <View style={styles.offlineIndicator}>
          <Text style={styles.offlineText}>Offline Mode - Some actions may be queued</Text>
        </View>
      )}

      <AttachmentCarouselModal {...carouselProps} />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  emptyContainer: {
    padding: 16,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 13,
    color: ATTACHMENT_COLORS.METADATA_COLOR,
    fontStyle: 'italic',
  },
  tableHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: ATTACHMENT_COLORS.TABLE_HEADER_BACKGROUND_COLOR,
    borderBottomWidth: 2,
    borderBottomColor: ATTACHMENT_COLORS.CARD_BORDER_COLOR,
    paddingVertical: 8,
    paddingHorizontal: 8,
  },
  nameHeaderColumn: {
    flex: 2,
    paddingRight: 12,
  },
  dateHeaderColumn: {
    width: 120,
    paddingHorizontal: 8,
  },
  sizeHeaderColumn: {
    width: 100,
    paddingHorizontal: 8,
  },
  actionsHeaderColumn: {
    width: 120,
    paddingHorizontal: 8,
  },
  headerText: {
    fontSize: 14,
    fontWeight: '600',
    color: ATTACHMENT_COLORS.FILENAME_COLOR,
  },
  listContainer: {
    flex: 1,
  },
  offlineIndicator: {
    backgroundColor: ATTACHMENT_COLORS.OFFLINE_BADGE_COLOR,
    padding: 8,
    alignItems: 'center',
  },
  offlineText: {
    fontSize: 12,
    color: '#fff',
    fontWeight: '500',
  },
});
